import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { getOrganizationSchema, getWebSiteSchema } from '../utils/seoSchemas';
import { Users, Shield, BookOpen, MapPin } from 'lucide-react';

const About = () => (
  <>
    <SEOHead
      title="About WorldCup Fan Planner | FIFA 2026 USA Travel Editorial"
      description="Who we are: an editorial team focused on FIFA World Cup 2026 travel in the United States—host cities, stadium days, itineraries, and how we use affiliates responsibly."
      schema={[getOrganizationSchema(), getWebSiteSchema()]}
    />
    <article className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">About WorldCup Fan Planner</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        We help fans plan trips to the 2026 FIFA World Cup with a USA-first lens: where to stay, how to move on match day, and how to chain cities without burning
        your budget. Canada and Mexico host great matches too—our tools cover North America—but our guides emphasize American host cities and stadium logistics.
      </p>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-amber-500" />
          Editorial approach (E-E-A-T)
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
          Articles are edited for clarity and practical travel value. Stadium and city pages combine data from our planner with first-person style tips (crowds,
          transit, weather) so you know what to expect—not just where a pin drops on a map. When schedules or venues change, we rely on{' '}
          <a href="https://www.fifa.com" className="text-amber-600 dark:text-amber-400 hover:underline" target="_blank" rel="noopener noreferrer">
            FIFA
          </a>{' '}
          and official host sources as the source of truth.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <MapPin className="h-5 w-5 text-amber-500" />
          What we build
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
          Host city and stadium guides, match listings, travel and AI itinerary tools, and long-form guides for budgets and multi-city trips. See{' '}
          <Link to="/guides" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            Travel guides
          </Link>{' '}
          for our latest USA-focused articles.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-amber-500" />
          Soccer rules content
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
          We publish one consolidated beginner guide—
          <Link to="/guides/soccer-rules-world-cup-beginners" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            Soccer rules for World Cup beginners
          </Link>
          —focused on what travelers need in the stands. For the full Laws of the Game, see the{' '}
          <a href="https://www.theifab.com/laws/" className="text-amber-600 dark:text-amber-400 hover:underline" target="_blank" rel="noopener noreferrer">
            IFAB Laws of the Game
          </a>
          .
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-500" />
          Ads, affiliates, and independence
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We may earn a commission when you book flights, hotels, or tours through partner links. Affiliate relationships do not change match data or official
          schedules on our site. We label commercial tools where helpful and disclose partnerships in line with our{' '}
          <Link to="/privacy-policy" className="text-amber-600 dark:text-amber-400 hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      <p className="text-sm text-gray-500 dark:text-gray-400">
        Questions?{' '}
        <Link to="/contact" className="text-amber-600 dark:text-amber-400 hover:underline">
          Contact us
        </Link>
        .
      </p>
    </article>
  </>
);

export default About;
